import DevErrorToast from "@/components/DevErrorToast"
import Toast from "@/components/Toast"
import {
  useOrderDeviceMutation,
  usePatientDetailPageDataQuery,
} from "@/data/queries"
import { HttpError } from "@/data/requests"
import { ClinicalProtocolType } from "@/model/ClinicalProtocol"
import { DeviceKit } from "@/model/DeviceOrders"
import { useState } from "react"
import { toast } from "sonner"
import ActionButton from "./ActionButton"
import OrderDeviceDialog from "./OrderDeviceDialog"

type OrderDeviceButtonProps = {
  bloomlifeId: string
  patientName: string
  attCompatibility?: boolean
  prescriptionId: string
  prescriptionType: ClinicalProtocolType
}

const OrderDeviceButton = ({
  bloomlifeId,
  patientName,
  attCompatibility,
  prescriptionId,
  prescriptionType,
}: OrderDeviceButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { data: patientData } = usePatientDetailPageDataQuery(bloomlifeId)
  const { mutateAsync: orderDevice, isPending: isOrdering } =
    useOrderDeviceMutation(bloomlifeId)

  const handleOrder = async (kit: DeviceKit) => {
    try {
      await orderDevice({
        prescriptionId,
        vendor: kit.vendor,
        deviceKitId: kit.id,
      })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Device ordered successfully." />
      ))
      setIsDialogOpen(false)
    } catch (error) {
      if ((error as HttpError)?.statusCode === 422) {
        toast.custom((toastId) => (
          <Toast
            toastId={toastId}
            variant="error"
            description="Invalid patient shipping address!"
          />
        ))
      } else if ((error as HttpError)?.statusCode === 400) {
        toast.custom((toastId) => (
          <Toast
            toastId={toastId}
            variant="error"
            description="Patient without a shipping address!"
          />
        ))
      } else {
        toast.custom(
          (toastId) => (
            <DevErrorToast
              toastId={toastId}
              userMessage="Something went wrong while ordering the device. Try again, or contact the dev team."
              error={error}
            />
          ),
          { duration: Infinity }
        )
      }
    }
  }

  // Get diagnosis from active prescriptions
  const activePrescription = patientData?.prescriptions.find(
    (p) =>
      p.id === patientData.patient.activeHypertensionPrescriptionId ||
      p.id === patientData.patient.activeDiabetesPrescriptionId
  )
  const diagnosis = activePrescription?.diagnosis || "-"

  return (
    <>
      <ActionButton onClick={() => setIsDialogOpen(true)}>Order</ActionButton>
      <OrderDeviceDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        patientName={patientName}
        shippingAddress={patientData?.patient.shippingAddress}
        attCompatibility={attCompatibility}
        diagnosis={diagnosis}
        onOrder={handleOrder}
        isOrdering={isOrdering}
        prescriptionTypes={[prescriptionType]}
      />
    </>
  )
}

export default OrderDeviceButton
